<template>
    <h1>
        <span class="mb-1 block text-xl lg:text-3xl">{{ product?.manufacturer?.translated?.name }}</span>
        <span class="font-mori mb-4 block text-xs leading-none tracking-[1.6px] uppercase lg:text-base!">
            {{ product?.translated?.name }} {{ colorVariantName }}
        </span>
    </h1>
    <div
        v-if="product?.calculatedPrice?.listPrice"
        class="flex items-center gap-2"
        :class="{
            'mb-2': product?.calculatedPrice?.listPrice?.price === product?.calculatedPrice?.totalPrice,
            'mb-4': product?.calculatedPrice?.listPrice?.price !== product?.calculatedPrice?.totalPrice,
        }">
        <p
            class="text-lg lg:text-xl"
            :class="{
                'text-base! leading-1 line-through':
                    product?.calculatedPrice?.listPrice?.price !== product?.calculatedPrice?.totalPrice,
            }">
            <Price :price="product?.calculatedPrice.listPrice.price" />
        </p>
        <p
            class="sale-tag-slim text-lg! lg:text-xl!"
            v-if="product?.calculatedPrice?.listPrice?.price !== product?.calculatedPrice?.totalPrice">
            <Price :price="product.calculatedPrice.totalPrice" />
        </p>
    </div>

    <ShopwareProductLoyaltySavingUsp
        v-if="product?.calculatedPrice?.listPrice?.price === product?.calculatedPrice?.totalPrice"
        :list-price="product.calculatedPrice.listPrice.price" />

    <div v-if="specifications.length > 0 && availableColorVariants?.length > 1">
        <p v-for="specification in specifications" class="mt-2 flex items-center gap-2 text-sm font-normal">
            <span class="font-semibold">
                {{ specification.group.translated.name }}
            </span>
            {{ specification.translated.name }}
        </p>
    </div>

    <ShopwareProductDetailColorVariants :colorVariants />

    <div class="mb-5">
        <ShopwareProductDetailAddToCart :product="product" :variants="variants" ref="addToCartComponent" />
    </div>
    <ShopwareProductDetailStickyAddToCart :add-to-cart-exposed="addToCartComponent" />

    <div class="pdpUsps flex max-w-fit flex-col gap-1 pb-5">
        <p v-for="uspKey in uspSnippetKeys" :key="uspKey" class="flex items-center text-sm font-normal">
            <CheckIcon class="mr-2 size-4 shrink-0 stroke-[2.5px] text-emerald-500" />
            <span v-html="snippets[uspKey]"></span>
        </p>
    </div>
    <div>
        <div :tabindex="1" class="mb-5">
            <div
                class="mb-5 flex cursor-pointer items-center justify-between border-b border-green-500 pb-5"
                @click="toggle(1)">
                <h3 class="text-sm">
                    {{ snippets['headless.pdp.productInformation'] }}
                </h3>
                <NuxtImg
                    :class="{ '-rotate-180': openIndex.includes(1) }"
                    class="size-4"
                    width="16"
                    height="16"
                    src="/icons/chevron-down.svg"
                    :alt="snippets['headless.icon.chevrondown']" />
            </div>
            <div
                class="overflow-hidden duration-300"
                :class="{
                    'max-h-screen!': openIndex.includes(1),
                    'max-h-0!': !openIndex.includes(1),
                }">
                <div
                    v-if="product.translated.description"
                    v-html="product.translated.description"
                    class="productDescription"></div>
                <div v-else>Geen product informatie beschikbaar</div>
            </div>
        </div>
        <div :tabindex="2" class="mb-5">
            <div
                class="mb-5 flex cursor-pointer items-center justify-between border-b border-green-500 pb-5"
                @click="toggle(2)">
                <h3 class="text-sm">
                    {{ snippets['headless.pdp.meterialInformation'] }}
                </h3>
                <NuxtImg
                    :class="{ '-rotate-180': openIndex.includes(2) }"
                    class="size-4"
                    width="16"
                    height="16"
                    src="/icons/chevron-down.svg"
                    :alt="snippets['headless.icon.chevrondown']" />
            </div>
            <div
                class="overflow-hidden duration-300"
                :class="{
                    'max-h-screen!': openIndex.includes(2),
                    'max-h-0!': !openIndex.includes(2),
                }">
                <p v-if="product.customFields?.material">
                    {{ product.customFields?.material }}
                </p>
                <p v-else>Geen informatie beschikbaar</p>
            </div>
        </div>
        <div :tabindex="3">
            <div
                class="mb-5 flex cursor-pointer items-center justify-between border-b border-green-500 pb-5"
                @click="toggle(3)">
                <h3 class="text-sm" v-html="snippets['headless.pdp.shippingAndReturnsTitle']"></h3>
                <NuxtImg
                    :class="{ '-rotate-180': openIndex.includes(3) }"
                    class="size-4"
                    width="16"
                    height="16"
                    src="/icons/chevron-down.svg"
                    :alt="snippets['headless.icon.chevrondown']" />
            </div>
            <div
                class="overflow-hidden duration-300"
                :class="{
                    'max-h-screen!': openIndex.includes(3),
                    'max-h-0!': !openIndex.includes(3),
                }">
                <ul class="list-inside list-disc text-sm lg:text-base">
                    <li v-html="snippets['headless.pdp.shippingAndReturns.1']"></li>
                    <li v-html="snippets['headless.pdp.shippingAndReturns.2']"></li>
                    <li v-html="snippets['headless.pdp.shippingAndReturns.3']"></li>
                    <li v-html="snippets['headless.pdp.shippingAndReturns.4']"></li>
                </ul>
            </div>
        </div>
    </div>
</template>
<script lang="ts" setup>
    import { CheckIcon } from '@heroicons/vue/24/outline';
    import ShopwareFasletWidget from './ShopwareFasletWidget.vue';
    import { overwriteLatestPageHistory } from '~/composables/usePageHistoryStorage';
    import { provideFasletCustomButtonTarget } from '~/composables/useFasletCustomButtonInjection';
    import { provideFasletPdpAddToCartHandler } from '~/composables/useFasletPdpAddToCart';
    import type { ProductDetailAddToCartExposed } from '~/composables/useProductDetailAddToCartExposed';
    import { computed } from 'vue';

    provideFasletCustomButtonTarget();
    const fasletPdpAddToCartHandler = provideFasletPdpAddToCartHandler();

    const snippetsStore = useSnippetsStore();
    const snippets = snippetsStore.snippets;

    const uspSnippetKeys = [
        'headless.pdp.uspOne',
        'headless.pdp.uspTwo',
        'headless.pdp.uspThree',
        'headless.pdp.uspFour',
    ];

    const route = useRoute();
    const { trackProductView } = useGTMEcommerce();
    const props = defineProps<{
        product?: any;
        variants?: any;
        colorVariants?: any[];
    }>();

    const availableColorVariants = computed(() => {
        return props.colorVariants?.filter(colorVariant => colorVariant.stock !== null && colorVariant.stock > 0) ?? [];
    });

    const specifications = computed(() =>
        props.product.properties.filter(property => property.group.visibleOnProductDetailPage === true),
    );

    const colorVariantName = computed((): string => {
        const colorProperty = props.product.properties.filter(property => property.group.translated.name === 'Kleur');
        return availableColorVariants.value?.length === 1 ? (colorProperty[0]?.name ?? '') : '';
    });

    function triggerEvent() {
        trackProductView(props.product);
    }

    onBeforeMount(() => {
        triggerEvent();
        overwriteLatestPageHistory({
            key: route.path,
            url: route.fullPath,
            type: 'productDetailPage',
            priority: 1,
        });
    });

    let openIndex = ref([]); // Initialize openIndex as an array
    openIndex.value.push(1); // open the first tab, in this case "product information"

    const toggle = index => {
        if (openIndex.value.includes(index)) {
            openIndex.value = openIndex.value.filter(i => i !== index);
        } else {
            openIndex.value.push(index);
        }
    };

    const addToCartComponent = ref<ProductDetailAddToCartExposed | null>(null);

    const handleAddToCart = async (variantId: string) => {
        const registered = fasletPdpAddToCartHandler.value;
        if (registered) {
            await registered(variantId);
            return;
        }
        if (addToCartComponent.value?.addToCart) {
            await addToCartComponent.value.addToCart(variantId);
        }
    };
</script>

<style>
    @reference "tailwindcss";
    .pdpUsps a {
        @apply text-green-700 underline;
    }
    .pdpUsps a:hover {
        @apply text-green-800;
    }
    .productDescription {
        h2 {
            @apply mb-2 text-base lg:text-lg;
        }
        ul {
            @apply list-inside list-disc text-sm lg:text-base;
        }
        ol {
            @apply list-inside list-decimal text-sm lg:text-base;
        }
        a {
            @apply text-green-700 underline;
        }
        a:hover {
            @apply text-green-800;
        }
        p {
            @apply mb-2 text-sm lg:text-base;
        }
    }
</style>
